import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { DISTRICTS } from "@/data/static";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { MapPin, Users, Calendar, ArrowLeft } from "lucide-react";

export default function District() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const meta = DISTRICTS.find(d => d.slug === slug);

  useEffect(() => {
    api.get(`/districts/${slug}`).then(r => setData(r.data)).catch(() => setData({ members: [], events: [] }));
  }, [slug]);

  if (!meta) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-10">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6"><ArrowLeft className="w-4 h-4" /> Back to homepage</Link>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-xl bg-brand-blue text-white font-mono font-bold text-xl flex items-center justify-center">{meta.abbr}</div>
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">District Unit</div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{meta.name}</h1>
              <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1"><MapPin className="w-3.5 h-3.5" /> Kerala, India</div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-5 mb-10">
            <KPI icon={Users} label="Paid & Active Members" value={data?.member_count ?? 0} />
            <KPI icon={Calendar} label="District Events" value={data?.events?.length ?? 0} />
            <KPI icon={MapPin} label="Local Bodies" value="—" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-black text-xl">District Committee</h2>
              </div>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li>Committee details will be populated by district admin.</li>
              </ul>
            </Card>
            <Card className="p-6">
              <h2 className="font-black text-xl mb-4">Upcoming Events</h2>
              <ul className="space-y-3">
                {(data?.events ?? []).map(e => (
                  <li key={e.id} className="border-b border-border pb-3 last:border-b-0">
                    <Badge variant="outline" className="text-[10px]">{e.type}</Badge>
                    <div className="font-semibold mt-1">{e.title}</div>
                    <div className="text-xs text-muted-foreground">{e.date} • {e.location}</div>
                  </li>
                ))}
                {(!data?.events || data.events.length === 0) && (
                  <li className="text-sm text-muted-foreground">No events scheduled.</li>
                )}
              </ul>
            </Card>
          </div>

          <Card className="p-6 mt-6 bg-muted/30 border-dashed">
            <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">Privacy Notice</div>
            <p className="text-sm text-muted-foreground mt-1">
              For member privacy, individual member details are not shown publicly. Only paid &amp; active member counts are shared. Authorised district and state office bearers can access the full directory after signing in.
            </p>
          </Card>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <Card className="p-5 hover-lift">
      <Icon className="w-5 h-5 text-brand-blue mb-2" />
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-black">{value}</div>
    </Card>
  );
}
